import {
  type LinkPickerPluginsConfiguration,
  useLinkPickerPlugins,
} from '@atlassian/link-picker-plugins';
import { useMemo } from 'react';
import { proxy, ref, useSnapshot } from 'valtio';

import { currentSiteData } from '@canvas/canvas-state/site-data';

type PluginsState = ReturnType<typeof useLinkPickerPlugins>;

const pluginsState = proxy<PluginsState & { lastUpdatedAt: number }>({
  plugins: ref([]),
  isLoading: true,
  lastUpdatedAt: Date.now(),
});

export const STABLE_FOR_TIME_IN_MILLIS = 60 * 1000;

export const useStablePlugins = (): PluginsState => {
  const site = useSnapshot(currentSiteData);
  const config: LinkPickerPluginsConfiguration = useMemo(
    () => ({
      cloudId: site.siteId,
      product: 'confluence', // This field determines the order of tabs, i.e. 'Confluence', 'Jira' & 'Atlas'
    }),
    [site.siteId]
  );

  const { plugins, isLoading } = useLinkPickerPlugins(config);
  const now = Date.now();

  const shouldRefresh = now - pluginsState.lastUpdatedAt >= STABLE_FOR_TIME_IN_MILLIS;
  if (shouldRefresh && !pluginsState.isLoading) {
    pluginsState.plugins = ref(plugins);
    pluginsState.isLoading = true;
    return pluginsState;
  }

  const changedPlugins = pluginsState.plugins.length < plugins.length;
  const changedIsLoading = pluginsState.isLoading === true && !isLoading;

  if (changedPlugins) {
    pluginsState.plugins = ref(plugins);
  }
  if (changedIsLoading) {
    pluginsState.isLoading = false;
  }

  if (changedPlugins || changedIsLoading) {
    pluginsState.lastUpdatedAt = Date.now();
  }

  return pluginsState;
};
